import { BASE_FIELD_MODS, UNLOCKABLE_FIELD_MODS, fieldModById } from "./field-mods";

// The Armory: permanent meta progression bought with ops points earned from
// runs. Unlocks never grant raw stats — they widen the field-mod draft pool
// or change how runs start, so every run is still won inside the run.

export type ArmoryUnlock = {
  id: string;
  name: string;
  desc: string;
  cost: number;
  // "mod-pool" adds field mods to the draft pool; "perk" changes run setup.
  kind: "mod-pool" | "perk";
  modIds?: string[];
  perk?: "opening-draft" | "fifth-man" | "second-wind" | "sensor-blackhot" | "sensor-eo";
};

export const ARMORY_UNLOCKS: ArmoryUnlock[] = [
  { id: "arm-tracer", name: "TRACER STOCKS", desc: "Adds BRIGHT TRACE to the draft pool", cost: 3, kind: "mod-pool", modIds: ["fm-cg-tracer"] },
  { id: "arm-barrels", name: "SPARE BARRELS", desc: "Adds STORM BARREL to the draft pool", cost: 5, kind: "mod-pool", modIds: ["fm-cg-storm"] },
  { id: "arm-autoloader", name: "AUTOLOADER KITS", desc: "Adds AUTOLOADER to the draft pool", cost: 7, kind: "mod-pool", modIds: ["fm-ac-auto"] },
  { id: "arm-battery", name: "BATTERY SPARES", desc: "Adds TWIN BATTERY to the draft pool", cost: 7, kind: "mod-pool", modIds: ["fm-how-battery"] },
  { id: "arm-plates", name: "PLATE SHIPMENT", desc: "Adds HARDENED PLATES to the draft pool", cost: 6, kind: "mod-pool", modIds: ["fm-hard-plates"] },
  { id: "arm-blockade", name: "AIR SUPERIORITY", desc: "Adds AIR BLOCKADE to the draft pool", cost: 8, kind: "mod-pool", modIds: ["fm-blockade"] },
  { id: "arm-opening", name: "ADVANCE REQUISITION", desc: "Start every operation with a free mod draft", cost: 10, kind: "perk", perk: "opening-draft" },
  { id: "arm-fifth", name: "FIFTH MAN", desc: "Operations start with a 5-man squad", cost: 12, kind: "perk", perk: "fifth-man" },
  // Sensor packages (docs/design/THEME.md: thermal is the identity; the
  // rest of the suite is earned). Base kit = DAYLIGHT + WHITE-HOT.
  { id: "arm-blackhot", name: "BLACK-HOT PACKAGE", desc: "Unlocks the BLACK-HOT sensor mode on all operations", cost: 4, kind: "perk", perk: "sensor-blackhot" },
  { id: "arm-eo", name: "EO-GREEN PACKAGE", desc: "Unlocks the EO-GREEN low-light sensor mode on all operations", cost: 4, kind: "perk", perk: "sensor-eo" },
];

export function armoryUnlockById(id: string): ArmoryUnlock | undefined {
  return ARMORY_UNLOCKS.find((u) => u.id === id);
}

export function canBuyUnlock(id: string, owned: ReadonlySet<string>, opsPoints: number): boolean {
  const unlock = armoryUnlockById(id);
  if (!unlock || owned.has(id)) return false;
  return opsPoints >= unlock.cost;
}

// The draft pool for a profile: base mods plus everything unlocked.
export function draftPoolIds(ownedUnlockIds: Iterable<string>): string[] {
  const pool = BASE_FIELD_MODS.map((m) => m.id);
  for (const id of ownedUnlockIds) {
    const unlock = armoryUnlockById(id);
    if (unlock?.kind !== "mod-pool" || !unlock.modIds) continue;
    for (const modId of unlock.modIds) {
      if (fieldModById(modId) && !pool.includes(modId)) pool.push(modId);
    }
  }
  return pool;
}

export function hasPerk(ownedUnlockIds: Iterable<string>, perk: NonNullable<ArmoryUnlock["perk"]>): boolean {
  for (const id of ownedUnlockIds) {
    if (armoryUnlockById(id)?.perk === perk) return true;
  }
  return false;
}

// Sensor cycle for a profile (docs/design/THEME.md §aesthetics: thermal is
// the identity, the rest of the suite is earned). BLACKOUT missions bypass
// this — a malfunction gives you whatever still works.
export type SensorMode = "daylight" | "white-hot" | "black-hot" | "eo-green";

export function sensorModesFor(ownedUnlockIds: Iterable<string>): SensorMode[] {
  const owned = [...ownedUnlockIds];
  const modes: SensorMode[] = ["daylight", "white-hot"];
  if (hasPerk(owned, "sensor-blackhot")) modes.push("black-hot");
  if (hasPerk(owned, "sensor-eo")) modes.push("eo-green");
  return modes;
}

// Sanity guard used by tests: every armory mod reference must resolve.
export function validateArmoryReferences(): string[] {
  const missing: string[] = [];
  for (const unlock of ARMORY_UNLOCKS) {
    for (const modId of unlock.modIds ?? []) {
      if (!fieldModById(modId)) missing.push(`${unlock.id} -> ${modId}`);
    }
  }
  // Unlockable mods that no armory unlock grants are unreachable content.
  for (const mod of UNLOCKABLE_FIELD_MODS) {
    const granted = ARMORY_UNLOCKS.some((u) => u.modIds?.includes(mod.id));
    if (!granted) missing.push(`unreachable mod ${mod.id}`);
  }
  return missing;
}
